import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  EmojiEvents as TrophyIcon,
  FitnessCenter as FitnessIcon,
  Flag as FlagIcon,
  Place as PlaceIcon,
  PersonOutline as PersonIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../auth/useAuth';
import { formatDateSafe } from '../../../shared/utils/dateHelpers';
import {
  NominationClientContext,
  NominationNominator,
  SubmitNominationResult,
  SuccessNominationRecord,
} from '../nomination.types';
import { submitNomination } from '../services/nominationService';
import {
  NOMINATION_FIELD_MAX_LENGTH,
  NominationValidationErrors,
  validateNominationFields,
} from '../utils/nominationValidation';
import { buildTrainingSnapshot, formatSessionsLabel, TrainingHistorySource } from '../utils/trainingHistory';
import { excerpt, normalizeClientName } from '../utils/nominationFilters';

export interface SubmitNominationDialogProps {
  open: boolean;
  onClose: () => void;
  client: NominationClientContext & TrainingHistorySource;
  /** The client's current success goal, shown as context for the nominator. */
  currentGoal?: string | null;
  /** Prior nominations visible to this user, used to flag recent duplicates. */
  recentNominations?: SuccessNominationRecord[];
  onSubmitted?: (result: SubmitNominationResult) => void;
}

interface NominationFormState {
  wins: string;
  comments: string;
  reason: string;
}

const EMPTY_FORM: NominationFormState = { wins: '', comments: '', reason: '' };

const FIELD_COPY: Array<{
  key: keyof NominationFormState;
  label: string;
  placeholder: string;
  required: boolean;
  rows: number;
}> = [
  {
    key: 'wins',
    label: 'Specific wins and achievements',
    placeholder: 'e.g. Dropped 6kg since January, first unassisted pull-up, completed the 10k they signed up for…',
    required: true,
    rows: 4,
  },
  {
    key: 'comments',
    label: 'General comments',
    placeholder: 'Anything else worth sharing: attitude, consistency, how they support other members, upcoming milestones…',
    required: false,
    rows: 3,
  },
  {
    key: 'reason',
    label: 'Why are you nominating this client?',
    placeholder: 'What makes this client stand out right now and why is this the moment to recognise them?',
    required: true,
    rows: 4,
  },
];

/**
 * Modal for nominating a client for Client Success from the Client Details Success tab.
 * Submission persists the nomination and notifies the client's location manager.
 *
 * @example
 * <SubmitNominationDialog open={open} onClose={close} client={client} onSubmitted={refresh} />
 */
export const SubmitNominationDialog: React.FC<SubmitNominationDialogProps> = ({
  open,
  onClose,
  client,
  currentGoal,
  recentNominations = [],
  onSubmitted,
}) => {
  const theme = useTheme();
  const { user, userRole, userMetadata } = useAuth();
  const [form, setForm] = useState<NominationFormState>(EMPTY_FORM);
  const [touched, setTouched] = useState<Record<keyof NominationFormState, boolean>>({
    wins: false,
    comments: false,
    reason: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const training = useMemo(() => buildTrainingSnapshot(client), [client]);
  const displayName = normalizeClientName(client.client_name) || client.client_name || 'this client';
  const firstName = displayName.split(' ')[0] || displayName;
  const location = client.primary_location?.trim() || '';
  const latestNomination = recentNominations[0];

  const errors: NominationValidationErrors = useMemo(() => validateNominationFields(form), [form]);
  const isValid = Object.keys(errors).length === 0;

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setTouched({ wins: false, comments: false, reason: false });
      setIsSubmitting(false);
      setSubmitError(null);
    }
  }, [open]);

  const nominator: NominationNominator | null = useMemo(() => {
    if (!user) return null;
    return {
      uid: user.uid,
      name: userMetadata?.displayName || user.displayName || user.email || 'Unknown staff',
      email: userMetadata?.email || user.email || null,
      role: userRole || 'USER',
      coachId: userMetadata?.coach_id ? String(userMetadata.coach_id) : null,
    };
  }, [user, userMetadata, userRole]);

  const handleFieldChange = (key: keyof NominationFormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setForm(current => ({ ...current, [key]: value }));
    if (submitError) setSubmitError(null);
  };

  const handleBlur = (key: keyof NominationFormState) => () => {
    setTouched(current => ({ ...current, [key]: true }));
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    setTouched({ wins: true, comments: true, reason: true });
    if (!isValid || !nominator) {
      if (!nominator) setSubmitError('You need to be signed in to submit a nomination.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const result = await submitNomination({
        client,
        training,
        wins: form.wins,
        comments: form.comments,
        reason: form.reason,
        nominator,
      });
      onSubmitted?.(result);
      onClose();
    } catch (error) {
      console.error('[success-nominations] submit failed', error);
      setSubmitError(
        error instanceof Error && error.message
          ? error.message
          : 'Unable to submit the nomination right now. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="submit-nomination-title"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle id="submit-nomination-title" sx={{ pb: 1.5 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
              color: '#fff',
            }}
          >
            <TrophyIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" component="span" display="block">
              Nominate {firstName} for Client Success
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {location
                ? `Your nomination is sent to the ${location} manager for review.`
                : 'This client has no primary location on record, so only admins will be notified.'}
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={handleClose} disabled={isSubmitting} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2.5 }}>
        <Stack spacing={2.5}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: alpha(theme.palette.primary.main, 0.03),
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, fontWeight: 600 }}>
                    {displayName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                      {displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID {client.client_id}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={7}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Tooltip
                    title={`${training.totalVisits} total visits · ${training.noShows} no-shows · ${training.lateCancels} late cancels`}
                  >
                    <Chip
                      icon={<FitnessIcon />}
                      label={`${formatSessionsLabel(training.sessionsCompleted)} completed`}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  </Tooltip>
                  {location && <Chip icon={<PlaceIcon />} label={location} size="small" variant="outlined" />}
                  {client.lead_coach_name && (
                    <Chip icon={<PersonIcon />} label={`Coach ${normalizeClientName(client.lead_coach_name)}`} size="small" variant="outlined" />
                  )}
                  {training.firstSessionDate && (
                    <Chip
                      label={`Since ${formatDateSafe(training.firstSessionDate, 'MMM yyyy', 'unknown')}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Grid>
              {currentGoal && (
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <FlagIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                    <Typography variant="body2" color="text.secondary">
                      <Box component="span" fontWeight={600} color="text.primary">
                        Current goal:
                      </Box>{' '}
                      {excerpt(currentGoal, 160)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
            </Grid>
          </Paper>

          {latestNomination && (
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
              Last nominated {formatDistanceToNow(latestNomination.created_at, { addSuffix: true })} by{' '}
              {latestNomination.nominated_by_name}
              {latestNomination.reason ? ` - “${excerpt(latestNomination.reason, 120)}”` : ''}. You can still submit a
              new nomination if there is a fresh milestone to recognise.
            </Alert>
          )}

          {submitError && (
            <Alert severity="error" onClose={() => setSubmitError(null)} sx={{ borderRadius: 2 }}>
              {submitError}
            </Alert>
          )}

          {FIELD_COPY.map(field => {
            const value = form[field.key];
            const fieldError = touched[field.key] ? errors[field.key] : undefined;
            return (
              <TextField
                key={field.key}
                label={field.required ? field.label : `${field.label} (optional)`}
                placeholder={field.placeholder}
                value={value}
                onChange={handleFieldChange(field.key)}
                onBlur={handleBlur(field.key)}
                required={field.required}
                multiline
                minRows={field.rows}
                fullWidth
                error={Boolean(fieldError)}
                helperText={fieldError || `${value.length}/${NOMINATION_FIELD_MAX_LENGTH}`}
                inputProps={{ maxLength: NOMINATION_FIELD_MAX_LENGTH, 'aria-label': field.label }}
                FormHelperTextProps={{ sx: { textAlign: fieldError ? 'left' : 'right', mr: 0 } }}
                disabled={isSubmitting}
              />
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !isValid}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <TrophyIcon />}
        >
          {isSubmitting ? 'Submitting…' : 'Submit nomination'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitNominationDialog;
